import React from "react";
import {
  Box,
  Grid,
  TextField,
  Typography,
  RadioGroup,
  Radio,
  FormControlLabel,
  Paper,
  MenuItem,
  Autocomplete,
} from "@mui/material";
import { Formik } from "formik";
import * as Yup from "yup";
import { useAnswers, useSaveAnswers } from "apiRequests/useAnswers";
import { useUniversitiesSearch } from "apiRequests/useUniversities";
import StepNav from "@/components/StepNav";

const Panel: React.FC<{ title: string; children: React.ReactNode }> = ({
  title,
  children,
}) => (
  <Paper elevation={2} sx={{ p: 3, mb: 4 }}>
    <Typography variant="h6" fontWeight="600" gutterBottom>
      {title}
    </Typography>
    {children}
  </Paper>
);

// Type-ahead search over the universities API; stores the chosen school name.
const SchoolAutocomplete: React.FC<{
  label: string;
  value: string;
  onChange: (val: string) => void;
}> = ({ label, value, onChange }) => {
  const { search } = useUniversitiesSearch();
  const [input, setInput] = React.useState(value || "");
  const [options, setOptions] = React.useState<string[]>([]);
  const [loading, setLoading] = React.useState(false);

  React.useEffect(() => {
    if (!input || input.length < 2) {
      setOptions([]);
      return;
    }
    const timer = setTimeout(async () => {
      setLoading(true);
      try {
        const results = await search(input);
        setOptions(Array.from(new Set(results.map((u) => u.name))));
      } catch {
        setOptions([]);
      }
      setLoading(false);
    }, 300);
    return () => clearTimeout(timer);
  }, [input]);

  return (
    <Autocomplete
      freeSolo
      options={options}
      loading={loading}
      inputValue={input}
      onInputChange={(_, v) => {
        setInput(v);
        onChange(v);
      }}
      renderInput={(params) => (
        <TextField
          {...params}
          label={label}
          fullWidth
          placeholder="Search for a school"
        />
      )}
    />
  );
};

export default function PersonalDetails() {
  const { data, error, loading, refetch, isReady } = useAnswers();
  const saveAnswers = useSaveAnswers();
console.log(data)
  return !isReady ? <p>Loading...</p>:(
    <Formik
      initialValues={{
        firstName: data?.personalInformation?.firstName || "",
        lastName: data?.personalInformation?.lastName || "",
        gender: data?.personalInformation?.gender || "",
        ethnicity: data?.personalInformation?.ethnicity || "",
        zip: data?.personalInformation?.zip || "",
        languages: data?.personalInformation?.languages || [],
        firstGen: data?.personalInformation?.firstGen || "",
        legacyChoice: data?.personalInformation?.legacyChoice || "none",
        fatherSchool: data?.personalInformation?.fatherSchool || "",
        motherSchool: data?.personalInformation?.motherSchool || "",
      }}
      validationSchema={Yup.object().shape({
        firstName: Yup.string().max(255).required("First name is required"),
        lastName: Yup.string().max(255).required("Last name is required"),
        zip: Yup.string().max(10),
      })}
      onSubmit={(values) => {
        // personalInformation is a single object the server overwrites
        // wholesale, and fields like jobs/leadership/awards now live on other
        // pages — merge over the latest server copy so we don't wipe them.
        // Save only; StepNav handles advancing to the next step.
        return saveAnswers({
          personalInformation: {
            ...data?.personalInformation,
            ...values,
          },
        }).then((results)=>{
          refetch()
          console.log("Answers saved", results)
        })
      }}
    >
      {({ values, handleChange, setFieldValue, handleSubmit, submitForm, validateForm }) => (
        <form onSubmit={handleSubmit}>
          <Box p={4}>
            {/* Personal Information */}
            <Panel title="Personal Information">
              <Grid container spacing={2} rowSpacing={5}>
                <Grid item xs={12} sm={6} md={4}>
                  <TextField
                    name="firstName"
                    label="First Name"
                    fullWidth
                    value={values.firstName}
                    onChange={handleChange}
                  />
                </Grid>
                <Grid item xs={12} sm={6} md={4}>
                  <TextField
                    name="lastName"
                    label="Last Name"
                    fullWidth
                    value={values.lastName}
                    onChange={handleChange}
                  />
                </Grid>
                <Grid item xs={12} sm={6} md={4}>
                  <TextField
                    select
                    name="gender"
                    label="Gender (Optional)"
                    fullWidth
                    value={values.gender}
                    onChange={handleChange}
                  >
                    <MenuItem value="">Prefer Not to Answer</MenuItem>
                    <MenuItem value="male">Male</MenuItem>
                    <MenuItem value="female">Female</MenuItem>
                    <MenuItem value="non-binary">Non Binary</MenuItem>
                  </TextField>
                </Grid>
                <Grid item xs={12} sm={6} md={4}>
                  <TextField
                    select
                    name="ethnicity"
                    label="Ethnicity (Optional)"
                    fullWidth
                    value={values.ethnicity}
                    onChange={handleChange}
                  >
                    <MenuItem value="">Prefer Not to Answer</MenuItem>
                    <MenuItem value="asian">Asian</MenuItem>
                    <MenuItem value="african-american">African American</MenuItem>
                    <MenuItem value="hispanic-latino">Hispanic or Latino</MenuItem>
                    <MenuItem value="white">White</MenuItem>
                  </TextField>
                </Grid>
                <Grid item xs={12} sm={6} md={4}>
                  <TextField
                    name="zip"
                    label="Zip Code"
                    fullWidth
                    value={values.zip}
                    onChange={handleChange}
                  />
                </Grid>
                <Grid item xs={12} sm={6} md={4}>
                  <Autocomplete
                    multiple
                    freeSolo
                    options={["English", "Spanish", "Chinese", "Hindi", "French", "German", "Arabic", "Portuguese", "Russian"]}
                    value={values.languages}
                    onChange={(_, newValue) => setFieldValue("languages", newValue)}
                    renderInput={(params) => (
                      <TextField
                        {...params}
                        label="Languages"
                        placeholder="Type or select languages"
                        helperText="Type a language and press Enter or Tab to add."
                      />
                    )}
                  />
                </Grid>
              </Grid>
            </Panel>

            {/* Legacy Information */}
            <Panel title="Legacy Information">
              <Grid container spacing={2} rowSpacing={5}>
                <Grid item xs={12}>
                  <Typography fontWeight="500">
                    Are you the first person in your immediate family to apply
                    to college?
                  </Typography>
                  <RadioGroup
                    row
                    name="firstGen"
                    value={values.firstGen}
                    onChange={handleChange}
                  >
                    <FormControlLabel
                      value="yes"
                      control={<Radio />}
                      label="Yes"
                    />
                    <FormControlLabel
                      value="no"
                      control={<Radio />}
                      label="No"
                    />
                  </RadioGroup>
                </Grid>

                {values.firstGen === "no" && (
                  <>
                    <Grid item xs={12}>
                      <Typography fontWeight="500">
                        Have your one or both parents attended the school you
                        want to apply for?
                      </Typography>
                      <RadioGroup
                        row
                        name="legacyChoice"
                        value={values.legacyChoice}
                        onChange={handleChange}
                      >
                        <FormControlLabel
                          value="father"
                          control={<Radio />}
                          label="Father"
                        />
                        <FormControlLabel
                          value="mother"
                          control={<Radio />}
                          label="Mother"
                        />
                        <FormControlLabel
                          value="both"
                          control={<Radio />}
                          label="Both"
                        />
                        <FormControlLabel
                          value="none"
                          control={<Radio />}
                          label="None"
                        />
                      </RadioGroup>
                    </Grid>

                    {(values.legacyChoice === "father" ||
                      values.legacyChoice === "both") && (
                      <Grid item xs={12} sm={6}>
                        <SchoolAutocomplete
                          label="Your Father's School"
                          value={values.fatherSchool}
                          onChange={(v) => setFieldValue("fatherSchool", v)}
                        />
                      </Grid>
                    )}

                    {(values.legacyChoice === "mother" ||
                      values.legacyChoice === "both") && (
                      <Grid item xs={12} sm={6}>
                        <SchoolAutocomplete
                          label="Your Mother's School"
                          value={values.motherSchool}
                          onChange={(v) => setFieldValue("motherSchool", v)}
                        />
                      </Grid>
                    )}
                  </>
                )}
              </Grid>
            </Panel>

            <StepNav
              onSave={async () => {
                const errors = await validateForm();
                if (Object.keys(errors).length > 0) {
                  submitForm(); // surface validation errors
                  return false;
                }
                await submitForm();
                return true;
              }}
            />
          </Box>
        </form>
      )}
    </Formik>
  );
}
